import { FoodRecognitionResponse, AIServiceError } from '../types/foodRecognition';
import { ExtractedPhotoRecipe } from '../types/photoImport';
import { convertImageToBase64, getFoodRecognitionPrompt, getCompressionConfigFromEnv, prepareImageForBanding, extractImageBand } from '../utils/imageProcessor';
import {
  RECEIPT_BAND_COUNT,
  computeBandGeometry,
  getBandPromptSuffix,
  stitchBandItems,
  mergeBandMetadata,
} from '../utils/receiptTiling';
import { getTestAnalysisData } from '../utils/developmentUtils';
import { getReceiptParserPrompt, convertReceiptToFoodRecognition, salvageReceiptJson, validateAndCleanReceipt, formatLocalDateISO, ReceiptRecognitionResponse } from '../utils/receiptProcessor';
import { getCurrentLanguage } from '../i18n';
import { trackLLMGeneration, trackEvent } from './analyticsService';
import { getAuthHeaders, ApiError, AUTH_REQUIRED_CODE, NETWORK_ERROR_CODE } from './apiClient';
// Defined beside `getAuthHeaders`, which is what throws them now (#778);
// re-exported so every screen keeps importing them from here.
export { AUTH_REQUIRED_CODE, NETWORK_ERROR_CODE, isAuthRequiredError } from './apiClient';
import { isNetworkError } from '../utils/fetchErrors';
import { getApiBaseUrl } from '../config/apiBase';

// Environment configuration
const USE_MOCK_AI = process.env.EXPO_PUBLIC_USE_MOCK_AI === 'true';
const DEBUG_AI = process.env.EXPO_PUBLIC_DEBUG_AI === 'true';
// Multi-band receipt scanning (see src/utils/receiptTiling.ts). On by default;
// set EXPO_PUBLIC_RECEIPT_TILING=false to fall back to a single full-image pass.
const RECEIPT_TILING_ENABLED = process.env.EXPO_PUBLIC_RECEIPT_TILING !== 'false';

/**
 * Progress callback. The optional second argument is a real 0..1 completion
 * fraction, reported only at milestones that have actually happened — the UI
 * uses it instead of guessing from a timer.
 */
export type ProgressReporter = (message: string, progress?: number) => void;

/** Where a recipe-photo scan is, for the screen to word in the cook's language. */
export type RecipePhotoStage = 'preparing' | 'analyzing';

// Milestones on the 0..1 progress scale.
const PROGRESS_IMAGE_READY = 0.1;
const PROGRESS_BANDS_START = 0.15;
const PROGRESS_MERGE_START = 0.9;

/** The code `api/_lib/aiQuotaGuard.ts` answers a spent daily allowance with. */
export const QUOTA_EXCEEDED_CODE = 'quota-exceeded';

/**
 * Did the day's AI allowance run out?
 *
 * The screen needs this to say so in the cook's own language, and to drop the
 * Retry button — retrying a refusal is what spent the rest of the ceiling in
 * #692.
 */
export function isQuotaExceededError(error: unknown): boolean {
  // Duck-typed rather than `instanceof ApiError` on purpose: `handleError`
  // re-wraps the throw before it reaches the screen, so the class is gone by
  // then but the code and status are carried across.
  if (!error || typeof error !== 'object') return false;
  const { code, status } = error as { code?: unknown; status?: unknown };
  return code === QUOTA_EXCEEDED_CODE || status === 429;
}

/**
 * Did the request never land?
 *
 * `isNetworkError` needs the original `TypeError`, and `handleError` re-wraps
 * the throw into a plain Error before the screen ever sees it — so the class
 * test has to happen here, while the original is still in hand, and the answer
 * travels the rest of the way as a code.
 */
export function isNetworkFailure(error: unknown): boolean {
  if (isNetworkError(error)) return true;
  if (!error || typeof error !== 'object') return false;
  return (error as { code?: unknown }).code === NETWORK_ERROR_CODE;
}

/**
 * Is a second attempt worth making?
 *
 * The band retry exists for answers that vary between draws: a truncated or
 * malformed model reply, a request that never landed. A 4xx is the server
 * saying no on purpose — it will say no again, and for the quota ceiling that
 * second no is not free, it is another charge against an allowance that is
 * already spent.
 */
function isRetryableBandFailure(error: unknown): boolean {
  if (error instanceof ApiError) {
    return error.status < 400 || error.status >= 500;
  }
  return true;
}

class AIService {
  constructor() {
    if (DEBUG_AI) {
      console.log('AIService - Configuration:', {
        useMock: USE_MOCK_AI,
        debug: DEBUG_AI,
      });
    }
    console.log(USE_MOCK_AI ? 'AIService - Using mock implementation' : 'AIService - Using server-side Gemini API');
  }

  /**
   * Analyzes an image for food items using the server-side Gemini route.
   * @param imageUri - The URI of the image to analyze
   * @param onProgress - Optional callback for progress updates
   * @param mode - Analysis mode: 'fridge' for refrigerator photos, 'receipt' for grocery receipts
   */
  async analyzeFoodImage(
    imageUri: string,
    onProgress?: ProgressReporter,
    mode: 'fridge' | 'receipt' = 'fridge'
  ): Promise<FoodRecognitionResponse> {
    console.log(`AIService - Analyzing ${mode} image:`, imageUri);

    try {
      if (USE_MOCK_AI) {
        return this.getMockResponse(imageUri, mode);
      }

      return await this.analyzeWithServer(imageUri, onProgress, mode);
    } catch (error) {
      // The message, not the Error: the bug reporter's console buffer runs
      // non-string args through JSON.stringify, and an Error's `message` is
      // non-enumerable, so this line read `{}` in #692 — the same trap already
      // worked around in parseGeminiResponse below.
      console.error(
        'AIService - Error analyzing image:',
        error instanceof Error ? error.message : String(error),
      );
      throw this.handleError(error);
    }
  }

  /**
   * POST one image to the analyze-image route and return the raw model text.
   */
  private async postAnalyzeImage(
    base64: string,
    mimeType: string,
    prompt: string,
    asJson: boolean,
  ): Promise<{ result: string; model?: string; input_tokens?: number; output_tokens?: number; latency_s?: number }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${getApiBaseUrl()}/api/ai/analyze-image`, {
      method: 'POST',
      headers,
      // responseFormat: 'json' asks the server to enforce JSON output at the
      // model level (receipts only — fridge mode keeps legacy behavior).
      body: JSON.stringify({ base64, mimeType, prompt, ...(asJson ? { responseFormat: 'json' } : {}) }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Unknown error' }));
      // ApiError, not a bare Error: the daily-quota refusal answers 429 with
      // `code: 'quota-exceeded'`, and dropping those two fields made it
      // indistinguishable from Safari's "Load failed". The band retry and the
      // single-pass fallback below both fired into a closed door because of it.
      throw new ApiError(
        err.error || `Server returned ${response.status}`,
        response.status,
        err.code,
      );
    }
    return response.json();
  }

  /**
   * Receipt scanning in overlapping bands.
   *
   * A single pass over a tall receipt gives the model very few image tokens per
   * printed line. Splitting the photo into `RECEIPT_BAND_COUNT` overlapping
   * slices and analysing them in parallel measurably improves capture
   * (83.8% → 90.5% item recall on the Costco fixture) and is faster in
   * wall-clock terms because the calls run concurrently. See
   * src/utils/receiptTiling.ts and `npm run eval:receipt`.
   *
   * Returns null when banding isn't possible, so the caller falls back to a
   * single full-image pass.
   */
  private async analyzeReceiptInBands(
    imageUri: string,
    prompt: string,
    quality: number,
    targetWidth: number,
    onProgress?: ProgressReporter,
  ): Promise<FoodRecognitionResponse | null> {
    // Scale once, then crop the bands out of the result. Cropping each band
    // from the original instead would decode the full-size photo per band.
    const prepared = await prepareImageForBanding(imageUri, targetWidth, quality);
    if (!prepared.width || !prepared.height) return null;

    const bands = computeBandGeometry(prepared.height, RECEIPT_BAND_COUNT);
    if (bands.length < 2) return null;

    onProgress?.(`Reading receipt in ${bands.length} sections...`, PROGRESS_BANDS_START);

    // Real progress: each band that lands moves the bar. Nothing here is on a
    // timer, so the UI cannot claim to be finished before the work is.
    let completed = 0;
    const results = await Promise.all(
      bands.map(async (band, index) => {
        try {
          const { base64, mimeType } = await extractImageBand(prepared.uri, band, prepared.width, quality);
          const bandPrompt = `${prompt}${getBandPromptSuffix(index, bands.length)}`;
          try {
            const data = await this.postAnalyzeImage(base64, mimeType, bandPrompt, true);
            return { parsed: this.parseGeminiResponse(data.result, 'receipt'), data };
          } catch (firstError) {
            // A refusal the server meant is not worth a second request, and a
            // quota refusal is worth less than nothing: it is another charge
            // against a ceiling that is already spent (#692).
            if (!isRetryableBandFailure(firstError)) throw firstError;
            // Losing a band is expensive: with 4 bands at 25% overlap only the
            // outer quarter of a band is seen by a neighbour, so the middle
            // ~50% of its lines are covered by nothing else (#658 lost 5 of 15
            // items this way). Sampling is non-deterministic, so one more draw
            // usually parses. Exactly one retry, to keep latency bounded.
            console.warn(
              `AIService - receipt band ${index + 1}/${bands.length} failed, retrying once: ` +
              `${firstError instanceof Error ? firstError.message : String(firstError)}`,
            );
            const data = await this.postAnalyzeImage(base64, mimeType, bandPrompt, true);
            return { parsed: this.parseGeminiResponse(data.result, 'receipt'), data };
          }
        } catch (error) {
          // The allowance is spent: every remaining band and the single-pass
          // fallback would be refused too, so sink the scan now and let the
          // screen say why. Dropping this band instead is what turned one
          // refusal into five (#692).
          if (isQuotaExceededError(error)) throw error;
          // A band that fails twice is dropped rather than sinking the whole
          // scan, but the caller reports the shortfall to the user.
          console.warn(
            `AIService - receipt band ${index + 1}/${bands.length} failed after retry: ` +
            `${error instanceof Error ? error.message : String(error)}`,
          );
          return null;
        } finally {
          completed += 1;
          const span = PROGRESS_MERGE_START - PROGRESS_BANDS_START;
          onProgress?.(
            `Read ${completed} of ${bands.length} sections...`,
            PROGRESS_BANDS_START + span * (completed / bands.length),
          );
        }
      }),
    );

    const ok = results.filter((r): r is NonNullable<typeof r> => r !== null);
    if (ok.length === 0) return null;

    trackLLMGeneration({
      $ai_trace_id: Math.random().toString(36).slice(2) + Date.now().toString(36),
      $ai_span_name: 'receipt_scan_tiled',
      $ai_model: ok[0].data.model ?? 'unknown',
      $ai_provider: 'google',
      $ai_input_tokens: ok.reduce((sum, r) => sum + (r.data.input_tokens || 0), 0),
      $ai_output_tokens: ok.reduce((sum, r) => sum + (r.data.output_tokens || 0), 0),
      $ai_latency: Math.max(...ok.map((r) => r.data.latency_s || 0)),
    });

    const { items: mergedItems, seamMerges } = stitchBandItems(ok.map((r) => r.parsed.detected_items));
    const mergedMetadata = mergeBandMetadata(ok.map((r) => r.parsed.receipt_metadata));
    // Re-run the deterministic safety net over the stitched set so cross-band
    // duplicates and any fee lines are cleaned once, globally.
    // 'max': these items came from overlapping bands, so a repeated entry is
    // the same printed line seen twice — summing would double-count it.
    const validation = validateAndCleanReceipt(mergedItems, mergedMetadata, { duplicateStrategy: 'max' });
    if (validation.warnings.length > 0) {
      console.warn('AIService - tiled receipt validation:', validation.warnings);
    }
    console.log(
      `AIService - tiled receipt: ${ok.length}/${bands.length} bands ok, ` +
      `${mergedItems.length} merged → ${validation.items.length} final items`,
    );

    // Band loss was invisible in production before #658 — trackLLMGeneration
    // above is computed from the surviving bands only, so a scan that dropped
    // one looked identical to a clean one. This is the signal that makes the
    // failure rate knowable.
    trackEvent('receipt_scan_completed', {
      bands_ok: ok.length,
      bands_total: bands.length,
      items: validation.items.length,
      // Seam lines two bands named differently (#750). A receipt format the
      // stitch handles badly shows up here before a cook reports it.
      seam_merges: seamMerges,
    });

    return {
      detected_items: validation.items,
      analysis_notes: {
        image_quality: 'Good',
        lighting_conditions: 'Good',
        total_items_detected: validation.items.length,
        areas_analyzed: [`Receipt (${ok.length} sections)`],
      },
      ...(mergedMetadata ? { receipt_metadata: mergedMetadata } : {}),
      validation_warnings: validation.warnings,
      capture_confidence: validation.captureConfidence,
      // Carried to the review screen so a partial scan is visible to the user
      // instead of looking like a complete one (#658).
      bands_ok: ok.length,
      bands_total: bands.length,
      scan_evidence: {
        band_reads: ok.map((r) => r.parsed.detected_items),
        seam_merges: seamMerges,
        model: ok[0].data.model,
      },
    };
  }

  private async analyzeWithServer(
    imageUri: string,
    onProgress?: ProgressReporter,
    mode: 'fridge' | 'receipt' = 'fridge'
  ): Promise<FoodRecognitionResponse> {
    const startTime = performance.now();

    const compressionStart = performance.now();
    onProgress?.('Preparing image for AI analysis...', 0.02);
    const envConfig = getCompressionConfigFromEnv();
    // Receipts are text-heavy documents — use higher fidelity than fridge photos
    // so small printed characters remain readable after compression, and
    // preserve the aspect ratio (forcing 3:4 shears the tiny printed text).
    const receiptDefaults = { maxWidth: 2000, maxHeight: 2667, quality: 0.85, preserveAspectRatio: true };
    const compressionConfig = mode === 'receipt'
      ? { ...receiptDefaults, ...envConfig }
      : envConfig;
    const currentLanguage = getCurrentLanguage();
    // Pass the user's local date so the AI can sanity-check the receipt's purchase date against "today".
    const prompt = mode === 'receipt'
      ? getReceiptParserPrompt(currentLanguage, formatLocalDateISO(new Date()))
      : getFoodRecognitionPrompt(currentLanguage);

    // Receipts: read the photo in overlapping bands (measurably better capture,
    // see analyzeReceiptInBands). Falls through to the single pass below if the
    // image can't be banded or every band fails.
    if (mode === 'receipt' && RECEIPT_TILING_ENABLED) {
      try {
        const tiled = await this.analyzeReceiptInBands(
          imageUri,
          prompt,
          compressionConfig.quality ?? 0.85,
          compressionConfig.maxWidth ?? 2000,
          onProgress,
        );
        if (tiled) {
          console.log(`⏱️ [COMPLETE] Tiled receipt analysis: ${((performance.now() - startTime) / 1000).toFixed(1)}s\n`);
          return tiled;
        }
        console.warn('AIService - tiled receipt scan unavailable, falling back to single pass');
      } catch (error) {
        // The fallback is another metered call on the same endpoint, so after a
        // refused band it can only be refused again. Let the caller see the
        // real reason rather than burying it under a second failure (#692).
        if (isQuotaExceededError(error)) throw error;
        console.warn(
          'AIService - tiled receipt scan failed, falling back to single pass: ' +
          `${error instanceof Error ? error.message : String(error)}`,
        );
      }
    }

    // Single-pass path only: the tiled path prepares its own (much cheaper)
    // band images, so compressing the whole photo here would be a full decode
    // and re-encode thrown away on every tiled receipt scan.
    const { base64, mimeType } = await convertImageToBase64(imageUri, compressionConfig);
    const compressionTime = (performance.now() - compressionStart) / 1000;
    console.log(`⏱️ [STEP 1] Image compression + base64: ${(compressionTime * 1000).toFixed(0)}ms`);
    onProgress?.(`Image prepared in ${compressionTime.toFixed(1)}s - Sending to AI...`, PROGRESS_IMAGE_READY);

    onProgress?.('Analyzing with AI...', PROGRESS_BANDS_START);

    const apiStart = performance.now();
    const data = await this.postAnalyzeImage(base64, mimeType, prompt, mode === 'receipt');
    const apiTime = (performance.now() - apiStart) / 1000;
    console.log(`⏱️ [STEP 2] Server-side Gemini response: ${(apiTime * 1000).toFixed(0)}ms (model: ${data.model})`);

    trackLLMGeneration({
      $ai_trace_id: Math.random().toString(36).slice(2) + Date.now().toString(36),
      $ai_span_name: mode === 'receipt' ? 'receipt_scan' : 'fridge_scan',
      $ai_model: data.model ?? 'unknown',
      $ai_provider: 'google',
      $ai_input_tokens: data.input_tokens,
      $ai_output_tokens: data.output_tokens,
      $ai_latency: data.latency_s,
    });

    const parsed = this.parseGeminiResponse(data.result, mode);
    const totalTime = (performance.now() - startTime) / 1000;
    console.log(`⏱️ [COMPLETE] Total analysis time: ${totalTime.toFixed(1)}s\n`);
    return parsed;
  }

  private parseGeminiResponse(text: string, mode: 'fridge' | 'receipt' = 'fridge'): FoodRecognitionResponse {
    try {
      // Clean up the response (remove markdown code blocks if present)
      let cleanedText = text.trim();
      if (cleanedText.startsWith('```json')) {
        cleanedText = cleanedText.replace(/^```json\s*/, '').replace(/\s*```$/, '');
      } else if (cleanedText.startsWith('```')) {
        cleanedText = cleanedText.replace(/^```\s*/, '').replace(/\s*```$/, '');
      }

      // Parse based on mode
      if (mode === 'receipt') {
        let receiptResponse: ReceiptRecognitionResponse;
        try {
          receiptResponse = JSON.parse(cleanedText) as ReceiptRecognitionResponse;
        } catch (jsonError) {
          // Malformed model output — a truncated tail, or a single item that
          // lost a brace (#658). Salvage the items that did parse instead of
          // failing the whole scan/band.
          const repaired = salvageReceiptJson(text);
          if (!repaired) throw jsonError;
          console.warn(
            `AIService - Receipt response was malformed (${jsonError instanceof Error ? jsonError.message : String(jsonError)}); ` +
            `recovered ${repaired.detected_items.length} items`,
          );
          receiptResponse = repaired;
        }
        console.log('AIService - Successfully parsed receipt response, items detected:', receiptResponse.detected_items.length);

        // Convert receipt response to standard FoodRecognitionResponse
        const convertedResponse = convertReceiptToFoodRecognition(receiptResponse);

        // Log expiry date info
        console.log('AIService - Items with expiry dates:', convertedResponse.detected_items
          .filter(item => item.expiry_date)
          .map(item => `${item.name}: ${JSON.stringify(item.expiry_date)}`)
        );

        return convertedResponse;
      } else {
        // Standard fridge mode
        const parsedResponse = JSON.parse(cleanedText) as FoodRecognitionResponse;
        console.log('AIService - Successfully parsed response, items detected:', parsedResponse.detected_items.length);

        // Always log expiry date info for debugging
        console.log('AIService - Items with expiry dates:', parsedResponse.detected_items
          .filter(item => item.expiry_date)
          .map(item => `${item.name}: ${JSON.stringify(item.expiry_date)}`)
        );

        if (DEBUG_AI) {
          console.log('AIService - Detected items:', parsedResponse.detected_items.map(item =>
            `${item.name} (${item.confidence}%)`
          ));
        }

        return parsedResponse;
      }
    } catch (parseError) {
      // Log the message, not the Error: the bug reporter's console buffer
      // serializes non-string args with JSON.stringify, and an Error stringifies
      // to `{}` — which is how #658 arrived with no parser message at all.
      console.error(
        'AIService - Failed to parse Gemini response as JSON:',
        parseError instanceof Error ? parseError.message : String(parseError),
      );
      console.error('AIService - Raw response:', text);
      throw new Error(`Invalid response format from AI service: ${parseError instanceof Error ? parseError.message : 'Parse error'}`);
    }
  }

  private async getMockResponse(imageUri?: string, mode: 'fridge' | 'receipt' = 'fridge'): Promise<FoodRecognitionResponse> {
    console.log(`AIService - Generating mock response for ${mode}:`, imageUri);

    // Check if this is the test fridge image or if we're using test images in DEV mode
    const isTestImage = imageUri && (
      imageUri.includes('our_fridge_left') ||
      imageUri.includes('test-images') ||
      imageUri.includes('assets/test-images')
    );

    if (isTestImage) {
      console.log('AIService - Using test fridge data for test image');
      try {
        const testData = await getTestAnalysisData();
        console.log('AIService - Loaded test data with', testData.detected_items.length, 'items');
        return testData;
      } catch (error) {
        console.warn('AIService - Failed to load test data, falling back to generic mock:', error);
      }
    }
    
    console.log('AIService - Using generic mock data...');
    
    // Simulate API delay
    return new Promise((resolve) => {
      setTimeout(() => {
        const mockResponse: FoodRecognitionResponse = {
          detected_items: [
            {
              name: "Quebon 2% Milk",
              category: "Dairy",
              confidence: 98,
              quantity_estimate: 'unknown' as const,
              visible_condition: "good",
              position: { x: 15, y: 25, width: 20, height: 35 },
              expiry_date: {
                days_until_expiry: 5,
                confidence: 85,
                explanation: "Dairy products typically last 5-7 days past opening, container appears recently opened"
              }
            },
            {
              name: "Red Bell Pepper",
              category: "Vegetables",
              confidence: 95,
              quantity_estimate: 1,
              visible_condition: "fresh",
              position: { x: 45, y: 30, width: 15, height: 20 },
              expiry_date: {
                days_until_expiry: 10,
                confidence: 78,
                explanation: "Fresh peppers typically last 1-2 weeks in refrigerator, item appears fresh"
              }
            },
            {
              name: "Leftover Pasta",
              category: "Leftovers",
              confidence: 85,
              quantity_estimate: 1,
              visible_condition: "good",
              position: { x: 70, y: 20, width: 18, height: 25 }
            },
            {
              name: "Greek Yogurt Container",
              category: "Dairy",
              confidence: 92,
              quantity_estimate: 1,
              visible_condition: "good",
              position: { x: 25, y: 65, width: 22, height: 30 },
              expiry_date: {
                days_until_expiry: 15,
                confidence: 90,
                explanation: "Unopened Greek yogurt has long shelf life, container appears sealed"
              }
            },
            {
              name: "Spinach in plastic bag",
              category: "Vegetables",
              confidence: 88,
              quantity_estimate: 1,
              visible_condition: "fresh",
              position: { x: 60, y: 70, width: 25, height: 20 }
            },
            {
              name: "Orange Juice Carton",
              category: "Beverages",
              confidence: 91,
              quantity_estimate: 'unknown' as const,
              visible_condition: "good",
              position: { x: 5, y: 10, width: 18, height: 40 }
            },
            {
              name: "Carrots",
              category: "Vegetables",
              confidence: 89,
              quantity_estimate: 2,
              visible_condition: "fresh",
              position: { x: 35, y: 75, width: 20, height: 15 }
            },
            {
              name: "Cheese Block",
              category: "Dairy",
              confidence: 93,
              quantity_estimate: 1,
              visible_condition: "good",
              position: { x: 8, y: 55, width: 15, height: 12 }
            },
            {
              name: "Butter",
              category: "Dairy",
              confidence: 96,
              quantity_estimate: 1,
              visible_condition: "good",
              position: { x: 75, y: 8, width: 12, height: 8 }
            },
            {
              name: "Eggs",
              category: "Dairy",
              confidence: 94,
              quantity_estimate: 12,
              visible_condition: "good",
              position: { x: 50, y: 10, width: 20, height: 15 }
            },
            {
              name: "Strawberry Jam",
              category: "Condiments",
              confidence: 87,
              quantity_estimate: 1,
              visible_condition: "good",
              position: { x: 82, y: 25, width: 10, height: 15 }
            },
            {
              name: "Apple",
              category: "Fruits",
              confidence: 76,
              quantity_estimate: 1,
              visible_condition: "slightly soft",
              position: { x: 40, y: 55, width: 12, height: 12 },
              expiry_date: {
                days_until_expiry: 2,
                confidence: 82,
                explanation: "Apple appears slightly soft, suggesting it may need to be consumed soon"
              }
            },
            {
              name: "Broccoli",
              category: "Vegetables",
              confidence: 82,
              quantity_estimate: 1,
              visible_condition: "fresh",
              position: { x: 78, y: 60, width: 15, height: 18 }
            },
            {
              name: "Expired Lunch Meat",
              category: "Meat",
              confidence: 88,
              quantity_estimate: 'unknown' as const,
              visible_condition: "near-expiry",
              position: { x: 12, y: 45, width: 18, height: 12 },
              expiry_date: {
                days_until_expiry: 0,
                confidence: 95,
                explanation: "Package shows clear expiry date that has passed, setting to expire today for safety"
              }
            }
          ],
          analysis_notes: {
            image_quality: "Good",
            lighting_conditions: "Fair",
            total_items_detected: 14,
            areas_analyzed: ["Main shelves", "Door compartments", "Crisper drawers"]
          }
        };
        
        console.log('AIService - Mock response generated with', mockResponse.detected_items.length, 'items');
        resolve(mockResponse);
      }, 2000); // 2 second delay to simulate API call
    });
  }

  private handleError(error: any): Error {
    const message = error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : 'Unknown error occurred during food analysis';

    const wrappedError = new Error(message);
    // Keep the server's own code when there is one. Overwriting it with
    // AI_SERVICE_ERROR is what left the screen unable to tell a spent allowance
    // from a dead network, and `details` held the raw Error — whose `message`
    // is non-enumerable, so the bug reporter serialized it as `{}` (#692).
    (wrappedError as any).code = error instanceof ApiError && error.code
      ? error.code
      : isNetworkError(error)
        ? NETWORK_ERROR_CODE
        : error instanceof Error
          ? 'AI_SERVICE_ERROR'
          : 'UNKNOWN_ERROR';
    (wrappedError as any).status = error instanceof ApiError ? error.status : undefined;
    (wrappedError as any).details = { message, ...(error instanceof ApiError ? { status: error.status } : {}) };
    return wrappedError;
  }

  /**
   * Generate content with a system instruction (single call, no chat)
   */
  async generateContentWithSystem(
    systemInstruction: string,
    userMessage: string,
    modelName?: string,
  ): Promise<string> {
    if (USE_MOCK_AI) {
      throw new Error('Gemini AI not initialized');
    }

    const genHeaders = await getAuthHeaders();
    const response = await fetch(`${getApiBaseUrl()}/api/ai/generate`, {
      method: 'POST',
      headers: genHeaders,
      body: JSON.stringify({ systemInstruction, userMessage, modelName }),
    });

    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new ApiError(
        err.error || `Server returned ${response.status}`,
        response.status,
        err.code,
      );
    }

    const data = await response.json();

    trackLLMGeneration({
      $ai_trace_id: Math.random().toString(36).slice(2) + Date.now().toString(36),
      $ai_span_name: 'text_generation',
      $ai_model: data.model,
      $ai_provider: 'google',
      $ai_input_tokens: data.input_tokens,
      $ai_output_tokens: data.output_tokens,
      $ai_latency: data.latency_s,
    });

    return data.result;
  }

  /** `onStage` gets a stage, not a sentence: the screen words it in the cook's language. */
  async analyzeRecipePhoto(
    imageUriOrUris: string | string[],
    onStage?: (stage: RecipePhotoStage) => void,
  ): Promise<{ recipe: ExtractedPhotoRecipe | null; confidence: number }> {
    const startTime = performance.now();
    const uris = Array.isArray(imageUriOrUris) ? imageUriOrUris : [imageUriOrUris];
    onStage?.('preparing');
    const compressed = await Promise.all(uris.map(uri => convertImageToBase64(uri)));
    onStage?.('analyzing');
    const headers = await getAuthHeaders();
    const response = await fetch(`${getApiBaseUrl()}/api/recipes/media-import`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ images: compressed.map(({ base64, mimeType }) => ({ base64, mimeType })) }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new ApiError(
        err.error || `Server returned ${response.status}`,
        response.status,
        err.code,
      );
    }
    const data = await response.json();
    trackLLMGeneration({
      $ai_trace_id: Math.random().toString(36).slice(2) + Date.now().toString(36),
      $ai_span_name: 'recipe_photo_import',
      $ai_model: data.llm_metrics?.model,
      $ai_provider: 'google',
      $ai_input_tokens: data.llm_metrics?.input_tokens,
      $ai_output_tokens: data.llm_metrics?.output_tokens,
      $ai_latency: data.llm_metrics?.latency_s,
    });
    const totalTime = (performance.now() - startTime) / 1000;
    console.log(`⏱️ Photo recipe import: ${totalTime.toFixed(1)}s`);
    if (!data.success || data.lowConfidence) {
      return { recipe: null, confidence: data.confidence ?? 0 };
    }
    return { recipe: data.recipe as ExtractedPhotoRecipe, confidence: data.confidence };
  }

  isUsingMock(): boolean {
    return USE_MOCK_AI;
  }

  getServiceStatus(): { ready: boolean, usingMock: boolean, hasApiKey: boolean, model: string } {
    return {
      ready: true,
      usingMock: USE_MOCK_AI,
      hasApiKey: true,
      model: 'gemini-2.5-flash',
    };
  }

  getCurrentModel(): string {
    return 'gemini-2.5-flash';
  }

  getAvailableModels(): string[] {
    return [
      'gemini-3-flash-preview',
      'gemini-2.5-flash',
      'gemini-2.5-flash-lite',
    ];
  }
}

// Export singleton instance
export const aiService = new AIService();
export default aiService;